/** biome-ignore-all lint/suspicious/noExplicitAny: any is needed here */

import { useLazyQuery } from '@apollo/client';
import { useThirdPartyFeesConfigValidationSchema } from '@openx/components-deal/lib/DealForm/hooks/useThirdPartyFeesConfigValidationSchema.ts';
import { getDealValidation } from '@openx/components-deal/lib/DealForm/validationSchema.ts';
import { useDealContext } from '@openx/components-deal/lib/utils/context/DealContext.tsx';
import type { UseValidationSchemaHook } from '@openx/components-deal/lib/utils/context/DealHookTypes.ts';
import { type DealFormDataWithTargeting, type DealMetaValue, dealFormField } from '@openx/types/deal.ts';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { object, string } from 'yup';
import { useAuth } from '../../../components/AuthContext/context.ts';
import { GetDealsListBasicDocument } from '../../../graphql/query/deals/GetDealsListBasic.generated.tsx';
import { dealFormState } from '../../../modules/graphql/cache.ts';
import { createPlatformFee94Test, createSegmentPartnerPlatformShareSchema } from './platformFeeValidation.ts';
import { getRboGroupFormat, getRootFormValueFromContext, rboMetaSchema } from './rboGroupValidation.ts';
import { createRetargetingAquaMetaTest, type RetargetingEnrichmentLookup } from './retargetingAquaValidation.ts';
import { validateFormatConsistency } from './validateFormatConsistency.ts';

function debounce<T extends (...args: any[]) => any>(
  fn: T,
  delay = 300
): (...args: Parameters<T>) => Promise<ReturnType<T> extends Promise<infer R> ? R : ReturnType<T>> {
  let timeoutId: ReturnType<typeof setTimeout>;

  return (...args: Parameters<T>): Promise<ReturnType<T> extends Promise<infer R> ? R : ReturnType<T>> => {
    if (timeoutId) clearTimeout(timeoutId);

    return new Promise((resolve, reject) => {
      timeoutId = setTimeout(() => {
        try {
          const result = fn(...args);

          if (result.then) {
            result.then(resolve);
            return;
          }

          resolve(result);
        } catch (error) {
          reject(error);
        }
      }, delay);
    });
  };
}

export const useValidationSchema: UseValidationSchemaHook = ({ initialDealState, isEdit }) => {
  const { session } = useAuth();
  const [dealResults] = useLazyQuery(GetDealsListBasicDocument);
  const { thirdPartyFeesConfigSchema } = useThirdPartyFeesConfigValidationSchema();
  const { t } = useTranslation();
  const {
    useCanEditSegmentPartnerPlatformShare,
    onBehalfOrganization,
    useDemandPartnersFetch,
    useOnBehalfOfAccountId,
  } = useDealContext();
  const canEditSegmentPartnerPlatformShare = useCanEditSegmentPartnerPlatformShare();
  const onBehalfOfAccountId = useOnBehalfOfAccountId();
  const { mapById: demandPartnersMapById, loading: demandPartnersLoading } = useDemandPartnersFetch({
    account_uid: onBehalfOfAccountId,
  });

  // Stable bag so we only clone+test rboMetaSchema once. Yup's `.test()` mutates the schema —
  // re-running it on every mapById/loading change would stack stale validators on the shared
  // rboMetaSchema export (and can let submit pass while the targeting alert still shows).
  const retargetingEnrichmentLookup = useMemo<RetargetingEnrichmentLookup>(() => ({ mapById: {}, loading: true }), []);
  retargetingEnrichmentLookup.mapById = demandPartnersMapById;
  retargetingEnrichmentLookup.loading = demandPartnersLoading;

  const userAccount =
    onBehalfOrganization?.exchange_account_network?.id ?? session?.account?.exchange_account_network_id;

  const debouncedAggregateFetch = useMemo(() => debounce(dealResults, 300), [dealResults]);

  const nameValidation = useMemo(() => {
    const { currentDeal } = dealFormState()();
    let lastValidatedName: string | undefined = currentDeal?.name;
    let lastResult = true;
    let pendingName: string | undefined;
    let pendingPromise: Promise<boolean> | undefined;

    return (inputDealName: string | undefined): boolean | Promise<boolean> => {
      if (!inputDealName) {
        return true;
      }

      const dealName = inputDealName.trim();

      if (pendingPromise && pendingName === dealName) {
        return pendingPromise;
      }

      // Value matches the last successfully validated name: return cached result.
      if (!pendingPromise && lastValidatedName === dealName) {
        return lastResult;
      }

      pendingName = dealName;

      const currentPromise: Promise<boolean> = debouncedAggregateFetch({
        fetchPolicy: 'network-only',
        variables: {
          queryString: `account_id=${encodeURIComponent(userAccount ?? '')}&name=${encodeURIComponent(dealName)}`,
        },
      }).then(({ data: { deals: dealsResults } = {} }) => {
        const isValid = !dealsResults?.objects?.length;

        if (pendingPromise === currentPromise) {
          lastValidatedName = dealName;
          lastResult = isValid;
          pendingPromise = undefined;
          pendingName = undefined;
        }

        return isValid;
      });

      pendingPromise = currentPromise;

      return currentPromise;
    };
  }, [debouncedAggregateFetch, userAccount]);

  const dealNameSchema = useMemo(
    () =>
      string()
        .required(t('Name is required'))
        .test('Verify Unique Deal Name', t('Deal Name is not unique'), nameValidation),
    [nameValidation, t]
  );

  const segmentPartnerPlatformShareSchema = useMemo(() => createSegmentPartnerPlatformShareSchema(t), [t]);

  const platformFee94Test = useMemo(() => createPlatformFee94Test(t), [t]);

  const retargetingAquaMetaSchema = useMemo(
    () => rboMetaSchema.clone().test(createRetargetingAquaMetaTest(retargetingEnrichmentLookup)),
    [retargetingEnrichmentLookup]
  );

  return useMemo(() => {
    const shape = {
      ...getDealValidation(t, isEdit, initialDealState),
      name: dealNameSchema,
      __meta: retargetingAquaMetaSchema,
      deal_id_type_radio: string(),
      external_deal_id: string()
        .when('deal_id_type_radio', {
          is: 'EXTERNAL',
          then: s => s.required(t('External deal id is required')),
          otherwise: s => s.notRequired(),
        })
        .test({
          name: 'externalDealFormatValidation',
          test: function (value) {
            if (!value) {
              return true;
            }

            const rootForm = getRootFormValueFromContext(this) as DealFormDataWithTargeting;
            if (!rootForm) {
              return true;
            }

            const meta = rootForm.__meta;
            const groupFormat = getRboGroupFormat(meta as DealMetaValue, rootForm);
            return validateFormatConsistency(
              groupFormat,
              rootForm,
              this,
              t('Ad placement does not match external deal format')
            );
          },
        }),
      third_party_fees_config: thirdPartyFeesConfigSchema,
    } satisfies Parameters<ReturnType<typeof object>['shape']>[0];

    if (canEditSegmentPartnerPlatformShare) {
      shape[dealFormField.SEGMENT_PARTNER_PLATFORM_SHARE] = segmentPartnerPlatformShareSchema;
    }

    return object()
      .shape(shape)
      .test(
        'platformFee94',
        t('Fees too high: the total of enabled percentage media fees exceeds the allowed limit. Reduce to continue.'),
        platformFee94Test
      );
  }, [
    canEditSegmentPartnerPlatformShare,
    dealNameSchema,
    initialDealState,
    isEdit,
    platformFee94Test,
    retargetingAquaMetaSchema,
    segmentPartnerPlatformShareSchema,
    t,
    thirdPartyFeesConfigSchema,
  ]);
};
